import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Stack,
  Grid,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

interface AgentPerformance {
  rank: number;
  name: string;
  ticketsClosed: number;
  successfulResolutions: number;
  escalations: number;
  resolutionRate: number;
  avgResolutionTime: string | null;
  performanceScore: number;
  badge: string | null;
}

interface AgentSummary {
  totalAgents: number;
  totalTicketsClosed: number;
  avgResolutionRate: string;
  avgResolutionTime: string | null;
  topPerformer: AgentPerformance | null;
}

const AgentPerformanceView: React.FC = () => {
  const [agents, setAgents] = useState<AgentPerformance[]>([]);
  const [summary, setSummary] = useState<AgentSummary | null>(null);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPerformance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/agent-performance?period=${period}`);
      
      if (!response.ok) throw new Error('Failed to fetch agent performance');
      
      const data = await response.json();
      setAgents(data.agents || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Error fetching agent performance:', err);
      setError('Failed to load agent performance data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const getPerformanceColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading && agents.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#080707' }}>
            🏆 Agent Performance Leaderboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#706E6B' }}>
            Track team productivity and resolution metrics
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={period}
              label="Time Period"
              onChange={(e) => setPeriod(e.target.value)}
            >
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchPerformance} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <TrophyIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.totalAgents}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Active Agents
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CheckCircleIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.totalTicketsClosed}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Tickets Closed
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <TrendingUpIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.avgResolutionRate}%
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Avg Resolution Rate
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <SpeedIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.avgResolutionTime ? `${summary.avgResolutionTime}h` : 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Avg Resolution Time
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Leaderboard Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Leaderboard Rankings
          </Typography>

          {agents.length === 0 && !loading ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No agent performance data available for this period
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Tickets Closed</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Successful</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Resolution Rate</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Avg Time</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Performance Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agents.map((agent) => (
                    <TableRow 
                      key={agent.name}
                      sx={{
                        bgcolor: agent.rank <= 3 ? 'rgba(255, 215, 0, 0.05)' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="h6" fontWeight={agent.rank <= 3 ? 700 : 600}>
                            #{agent.rank}
                          </Typography>
                          {agent.badge && (
                            <Typography fontSize="1.5rem">{agent.badge}</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ 
                            bgcolor: agent.rank === 1 ? 'gold' : 
                                    agent.rank === 2 ? 'silver' : 
                                    agent.rank === 3 ? '#CD7F32' : 'primary.main'
                          }}>
                            {agent.name.substring(0, 2).toUpperCase()}
                          </Avatar>
                          <Typography fontWeight={600}>{agent.name}</Typography>
                        </Stack>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Chip 
                          label={agent.ticketsClosed} 
                          color="primary" 
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      
                      <TableCell align="center">
                        <Chip 
                          label={agent.successfulResolutions} 
                          color="success" 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      
                      <TableCell align="center">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {agent.resolutionRate.toFixed(1)}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={agent.resolutionRate} 
                            color={agent.resolutionRate >= 80 ? 'success' : agent.resolutionRate >= 60 ? 'warning' : 'error'}
                            sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                          />
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Typography variant="body2">
                          {agent.avgResolutionTime ? `${agent.avgResolutionTime}h` : 'N/A'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Tooltip title={`Performance: ${agent.performanceScore}/100`}>
                          <Chip 
                            label={agent.performanceScore}
                            color={getPerformanceColor(agent.performanceScore) as any}
                            sx={{ fontWeight: 700, minWidth: 50 }}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AgentPerformanceView;

